import { useMemo } from 'react'
import { MultiSelect, Select, Stack, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { getTimezone } from '@/utils/datetime'
import type {
  AwareDatetimeInterval,
  CitySenseBackendDriveManagementDriveStateDriveState,
  DriveBillingType,
  DriveType,
} from '@/api/main/types.gen'

export type DriveStateValue = CitySenseBackendDriveManagementDriveStateDriveState

export const ALL_DRIVE_STATES: DriveStateValue[] = [
  'pending',
  'in_progress',
  'finished',
  'cancelled',
  'cancelled_in_progress',
]

export const ALL_BILLING_TYPES: DriveBillingType[] = ['billable', 'non_billable', 'development', 'disrespect_all']

export const ALL_DRIVE_TYPES: DriveType[] = ['statistical', 'evidence', 'documentation', 'testing', 'special']

export type DriveMapFilterValues = {
  subInterval: AwareDatetimeInterval | null
  states: DriveStateValue[]
  billingTypes: DriveBillingType[]
  driveTypes: DriveType[]
}

// Empty arrays mean "no constraint". Vehicles are filtered via the legend
// checkboxes, not here.
export const defaultDriveMapFilters = (): DriveMapFilterValues => ({
  subInterval: null,
  states: [],
  billingTypes: [],
  driveTypes: [],
})

const intervalKey = (i: AwareDatetimeInterval) => `${i.starting_from}__${i.ending_at}`
const formatSubInterval = (i: AwareDatetimeInterval) => {
  const tz = getTimezone()
  return `${dayjs.utc(i.starting_from).tz(tz).format('ddd DD.MM HH:mm')} – ${dayjs
    .utc(i.ending_at)
    .tz(tz)
    .format('HH:mm')}`
}

// The modal sits at zIndex 10000; combobox dropdowns default to ~300 and would
// otherwise render behind the overlay.
const DROPDOWN_PROPS = { withinPortal: true, zIndex: 10001 } as const

function EnumMultiSelect<T extends string>({
  label,
  options,
  value,
  onChange,
  renderLabel,
}: {
  label: string
  options: readonly T[]
  value: T[]
  onChange: (next: T[]) => void
  renderLabel: (v: T) => string
}) {
  const data = useMemo(() => options.map(v => ({ value: v, label: renderLabel(v) })), [options, renderLabel])
  return (
    <MultiSelect
      label={label}
      data={data}
      value={value}
      onChange={next => onChange(next as T[])}
      clearable
      comboboxProps={DROPDOWN_PROPS}
    />
  )
}

export function DriveMapFilters({
  selectableIntervals,
  filters,
  onFiltersChange,
}: {
  selectableIntervals: AwareDatetimeInterval[]
  filters: DriveMapFilterValues
  onFiltersChange: (next: DriveMapFilterValues) => void
}) {
  const { t } = useTranslation('scheduling')

  const intervalOptions = useMemo(
    () => selectableIntervals.map(i => ({ value: intervalKey(i), label: formatSubInterval(i) })),
    [selectableIntervals]
  )

  return (
    <Stack gap="sm">
      <Text fw={600} size="sm">
        {t('filters', { ns: 'common' })}
      </Text>

      <Select
        label={t('subInterval')}
        placeholder={t('allIntervals')}
        data={intervalOptions}
        value={filters.subInterval ? intervalKey(filters.subInterval) : null}
        onChange={v => {
          const next = selectableIntervals.find(i => intervalKey(i) === v) ?? null
          onFiltersChange({ ...filters, subInterval: next })
        }}
        clearable
        searchable
        comboboxProps={DROPDOWN_PROPS}
      />

      <EnumMultiSelect
        label={t('driveState')}
        options={ALL_DRIVE_STATES}
        value={filters.states}
        onChange={states => onFiltersChange({ ...filters, states })}
        renderLabel={v => t(`driveStates.${v}`, { defaultValue: v })}
      />

      <EnumMultiSelect
        label={t('billingType')}
        options={ALL_BILLING_TYPES}
        value={filters.billingTypes}
        onChange={billingTypes => onFiltersChange({ ...filters, billingTypes })}
        renderLabel={v => t(`billingTypes.${v}`, { defaultValue: v })}
      />

      <EnumMultiSelect
        label={t('driveType')}
        options={ALL_DRIVE_TYPES}
        value={filters.driveTypes}
        onChange={driveTypes => onFiltersChange({ ...filters, driveTypes })}
        renderLabel={v => t(`driveTypes.${v}`, { defaultValue: v })}
      />
    </Stack>
  )
}
